import { createMachine } from 'xstate';

import shared from 'common/bento/shared';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import { AnyMachineEvent } from 'features/Bento/types/Abstract';

import actions from './actions';
import guards, { Guard } from './guards';
import model, { Context } from './model';
import services, { Service } from './services';
import { AddressState, State } from './state';

const machine = createMachine<Context, AnyMachineEvent>(
  {
    context: model.initialContext,

    id: 'capitalDepositQuestions',

    initial: State.INIT,

    // TODO(LED-1798): investigate why the following events bubble up to the top:
    // done.invoke.sendCompanyAddress and done.invoke.sendSameAddress
    // on: {
    //   '*': {
    //     actions: shared.actions.unhandled(),
    //   },
    // },

    states: {
      [State.INIT]: {
        always: [
          {
            cond: Guard.HasCompletedQuestions,
            target: State.DRAFT_STATUTES,
          },
          {
            target: State.INTRO,
          },
        ],
      },

      [State.INTRO]: {
        entry: shared.actions.trackView(),

        on: {
          NEXT: State.COMPANY_LEGAL_INFO,
          PREV: State.ABORT,
        },
      },

      [State.COMPANY_LEGAL_INFO]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.INTRO,
          SUBMIT_COMPANY_LEGAL_INFO: State.SEND_COMPANY_LEGAL_INFO,
        },
      },
      [State.SEND_COMPANY_LEGAL_INFO]: {
        invoke: {
          onDone: State.CAPITAL_INFO,
          onError: {
            actions: shared.actions.error(),
            target: State.COMPANY_LEGAL_INFO,
          },
          src: Service.SendCompanyLegalInfo,
        },
      },

      [State.CAPITAL_INFO]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.COMPANY_LEGAL_INFO,
          SUBMIT_CAPITAL_INFO: State.SEND_CAPITAL_INFO,
        },
      },
      [State.SEND_CAPITAL_INFO]: {
        invoke: {
          onDone: [
            {
              cond: Guard.IsMandatee,
              target: State.ADDRESS,
            },
            {
              cond: Guard.IsConfirmedPartner,
              target: State.ADDRESS,
            },
            { target: State.HAS_PARTNER },
          ],
          onError: {
            actions: shared.actions.error(),
            target: State.CAPITAL_INFO,
          },
          src: Service.SendCapitalInfo,
        },
      },

      [State.HAS_PARTNER]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.CAPITAL_INFO,
          SUBMIT_HAS_PARTNER: [
            {
              cond: Guard.HasPartner,
              target: State.PARTNER_NAME,
            },
            {
              target: State.SEND_HAS_NO_PARTNER,
            },
          ],
        },
      },
      [State.SEND_HAS_NO_PARTNER]: {
        invoke: {
          onDone: State.ADDRESS,
          onError: {
            actions: shared.actions.error(),
            target: State.HAS_PARTNER,
          },
          src: Service.SendHasNoPartner,
        },
      },

      [State.PARTNER_NAME]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.HAS_PARTNER,
          SUBMIT_PARTNER_NAME: State.SEND_PARTNER_NAME,
        },
      },
      [State.SEND_PARTNER_NAME]: {
        invoke: {
          onDone: State.ADDRESS,
          onError: {
            actions: shared.actions.error(),
            target: State.PARTNER_NAME,
          },
          src: Service.SendPartner,
        },
      },

      [State.ADDRESS]: {
        initial: AddressState.INIT,
        onDone: State.CERTIFICATE_WARNING,

        states: {
          [AddressState.INIT]: {
            always: [
              {
                cond: Guard.HasNotFilledPersonalAddress,
                target: AddressState.COMPANY_ADDRESS,
              },
              {
                cond: Guard.IsMandatee,
                target: AddressState.COMPANY_ADDRESS,
              },
              {
                target: AddressState.SAME_ADDRESS,
              },
            ],
          },

          [AddressState.SAME_ADDRESS]: {
            entry: shared.actions.trackView(),

            on: {
              PREV: [
                {
                  cond: Guard.IsConfirmedPartner,
                  target: `#capitalDepositQuestions.${State.CAPITAL_INFO}`,
                },
                { target: `#capitalDepositQuestions.${State.HAS_PARTNER}` },
              ],
              SUBMIT_SAME_ADDRESS: [
                {
                  cond: Guard.HasSameAddress,
                  target: AddressState.SEND_SAME_ADDRESS,
                },
                {
                  target: AddressState.COMPANY_ADDRESS,
                },
              ],
            },
          },
          [AddressState.SEND_SAME_ADDRESS]: {
            invoke: {
              onDone: AddressState.DONE,
              onError: {
                actions: shared.actions.error(),
                target: AddressState.SAME_ADDRESS,
              },
              src: Service.SendSameAddress,
            },
          },

          [AddressState.COMPANY_ADDRESS]: {
            entry: shared.actions.trackView(),

            on: {
              PREV: [
                {
                  cond: Guard.IsMandatee,
                  target: `#capitalDepositQuestions.${State.CAPITAL_INFO}`,
                },
                { target: AddressState.SAME_ADDRESS },
              ],
              SUBMIT_COMPANY_ADDRESS: AddressState.SEND_COMPANY_ADDRESS,
            },
          },
          [AddressState.SEND_COMPANY_ADDRESS]: {
            invoke: {
              onDone: AddressState.DONE,
              onError: {
                actions: shared.actions.error(),
                target: AddressState.COMPANY_ADDRESS,
              },
              src: Service.SendCompanyAddress,
            },
          },

          [AddressState.DONE]: {
            type: 'final',
          },
        },
      },

      [State.CERTIFICATE_WARNING]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.ADDRESS,
          SUBMIT_CAPITAL_DEPOSIT_QUESTIONS:
            State.SEND_CAPITAL_DEPOSIT_QUESTIONS,
        },
      },
      [State.SEND_CAPITAL_DEPOSIT_QUESTIONS]: {
        invoke: {
          onDone: State.DRAFT_STATUTES,
          onError: {
            actions: shared.actions.error(),
            target: State.CERTIFICATE_WARNING,
          },
          src: Service.SendCapitalDepositQuestions,
        },
      },

      [State.DRAFT_STATUTES]: {
        on: {
          NEXT: State.DONE,
          PREV: State.CERTIFICATE_WARNING,
        },
      },

      [State.ABORT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },

      [State.DONE]: {
        data: {
          status: BentoModuleDoneStatus.DONE,
        },
        type: 'final',
      },
    },
  },

  {
    actions,
    guards,
    services,
  },
);

export default machine;
